import React, { createContext, useContext, useRef, type ReactNode } from 'react';

export interface FormDebugTelemetry {
  containerWidth: number | undefined;
  columnCount: number;
  columnGapPx: number;
  trackWidthPx: number | null;
  solverLogs: string[];
}

interface FormEngineDebugContextValue {
  enabled: boolean;
  showCards: boolean;
  showDebugBadges: boolean;
  /** Mutable ref to collect solver logs across renders. */
  logCollector: React.MutableRefObject<string[]>;
  /** Called by FormFieldsRenderer to push telemetry up. */
  onTelemetry?: (telemetry: FormDebugTelemetry) => void;
  /** Currently selected field name (for selection ring). Null = none. */
  selectedFieldName: string | null;
  /** Called when a field is clicked in debug mode. */
  onFieldClick?: (fieldName: string) => void;
}

const FormEngineDebugContext = createContext<FormEngineDebugContextValue>({
  enabled: false,
  showCards: false,
  showDebugBadges: false,
  logCollector: { current: [] },
  selectedFieldName: null,
});

export function useFormEngineDebug(): FormEngineDebugContextValue {
  return useContext(FormEngineDebugContext);
}

interface ProviderProps {
  children: ReactNode;
  enabled: boolean;
  showCards?: boolean;
  showDebugBadges?: boolean;
  onTelemetry?: (telemetry: FormDebugTelemetry) => void;
  selectedFieldName?: string | null;
  onFieldClick?: (fieldName: string) => void;
}

export function FormEngineDebugProvider({
  children,
  enabled,
  showCards = false,
  showDebugBadges = true,
  onTelemetry,
  selectedFieldName = null,
  onFieldClick,
}: ProviderProps): React.ReactElement {
  const logCollector = useRef<string[]>([]);
  const onTelemetryRef = useRef(onTelemetry);
  onTelemetryRef.current = onTelemetry;
  const onFieldClickRef = useRef(onFieldClick);
  onFieldClickRef.current = onFieldClick;

  const stableOnTelemetry = React.useCallback(
    (t: FormDebugTelemetry) => onTelemetryRef.current?.(t),
    [],
  );
  const stableOnFieldClick = React.useCallback(
    (name: string) => onFieldClickRef.current?.(name),
    [],
  );

  const value = React.useMemo<FormEngineDebugContextValue>(
    () => ({
      enabled,
      showCards,
      showDebugBadges,
      logCollector,
      onTelemetry: stableOnTelemetry,
      selectedFieldName,
      onFieldClick: stableOnFieldClick,
    }),
    [enabled, showCards, showDebugBadges, stableOnTelemetry, selectedFieldName, stableOnFieldClick],
  );

  return (
    <FormEngineDebugContext.Provider value={value}>
      {children}
    </FormEngineDebugContext.Provider>
  );
}
